/**
 * Jibbitz Personalization - Interactive Shoe Customization
 * Allows users to drag and drop Jibbitz charms onto Crocs shoes
 *
 * FLOW:
 * 1. User clicks "Customize with Jibbitz" on product page
 * 2. User is redirected to Jibbitz collection to select charms
 * 3. User selects Jibbitz and clicks "Continue to Personalization"
 * 4. User is redirected to personalization page with selected Jibbitz loaded
 */

// Storage keys
const STORAGE_KEYS = {
  SELECTED_SHOE: "jibbitz_selected_shoe",
  SELECTED_JIBBITZ: "jibbitz_selected_charms",
};

/**
 * Jibbitz Collection Selector
 * Adds selection functionality to the Jibbitz collection page
 */
class JibbitzCollectionSelector extends HTMLElement {
  constructor() {
    super();
    this.selectedJibbitz = new Map(); // variantId -> product data
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Check if we have a shoe saved (coming from product page)
    const savedShoe = sessionStorage.getItem(STORAGE_KEYS.SELECTED_SHOE);

    if (savedShoe) {
      this.shoe = JSON.parse(savedShoe);
    } else {
      // No shoe selected - still allow selection, just change the message
      this.shoe = null;
    }

    this.createSelectionUI();
    this.bindProductCards();
  }

  createSelectionUI() {
    // Create floating selection bar
    this.selectionBar = document.createElement("div");
    this.selectionBar.className = "jibbitz-selection-bar";

    const shoeInfo = this.shoe
      ? `<div class="jibbitz-selection-bar__shoe">
          <img src="${this.shoe.image}" alt="${this.shoe.title}">
          <span>Customizing: ${this.shoe.title}</span>
        </div>`
      : `<div class="jibbitz-selection-bar__shoe">
          <span>Select Jibbitz charms to personalize your Crocs</span>
        </div>`;

    this.selectionBar.innerHTML = `
      <div class="jibbitz-selection-bar__info">
        ${shoeInfo}
        <div class="jibbitz-selection-bar__count">
          <span class="count">0</span> Jibbitz selected
        </div>
      </div>
      <div class="jibbitz-selection-bar__actions">
        <button class="jibbitz-selection-bar__clear" type="button">Clear All</button>
        <button class="jibbitz-selection-bar__continue" type="button" disabled>
          Continue to Personalization
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(this.selectionBar);

    // Bind bar events
    this.selectionBar
      .querySelector(".jibbitz-selection-bar__clear")
      .addEventListener("click", () => this.clearSelection());
    this.selectionBar
      .querySelector(".jibbitz-selection-bar__continue")
      .addEventListener("click", () => this.continueToPersonalization());

    // Add styles
    this.addStyles();
  }

  addStyles() {
    if (document.getElementById("jibbitz-selector-styles")) return;

    const styles = document.createElement("style");
    styles.id = "jibbitz-selector-styles";
    styles.textContent = `
      .jibbitz-selection-bar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #fff;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
        padding: 16px 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        z-index: 9999;
        transform: translateY(100%);
        transition: transform 0.3s ease;
      }
      .jibbitz-selection-bar.visible {
        transform: translateY(0);
      }
      .jibbitz-selection-bar__info {
        display: flex;
        align-items: center;
        gap: 24px;
      }
      .jibbitz-selection-bar__shoe {
        display: flex;
        align-items: center;
        gap: 12px;
        font-weight: 500;
      }
      .jibbitz-selection-bar__shoe img {
        width: 50px;
        height: 50px;
        object-fit: contain;
        border-radius: 8px;
        background: #f5f5f5;
      }
      .jibbitz-selection-bar__count {
        color: #666;
        font-size: 15px;
      }
      .jibbitz-selection-bar__count .count {
        font-weight: 700;
        color: #0077c8;
        font-size: 1.4em;
      }
      .jibbitz-selection-bar__actions {
        display: flex;
        gap: 12px;
      }
      .jibbitz-selection-bar__clear {
        padding: 12px 20px;
        border: 1px solid #ddd;
        background: #fff;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s;
      }
      .jibbitz-selection-bar__clear:hover {
        background: #f5f5f5;
      }
      .jibbitz-selection-bar__continue {
        padding: 14px 28px;
        background: linear-gradient(135deg, #0077c8, #00a1e0);
        color: #fff;
        border: none;
        border-radius: 30px;
        cursor: pointer;
        font-weight: 600;
        font-size: 15px;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(0,119,200,0.3);
      }
      .jibbitz-selection-bar__continue:disabled {
        background: #ccc;
        cursor: not-allowed;
        box-shadow: none;
      }
      .jibbitz-selection-bar__continue:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0,119,200,0.4);
      }

      /* Product card selection state - works with both class and custom elements */
      .jibbitz-selectable {
        cursor: pointer;
        position: relative !important;
      }
      .jibbitz-selectable::before {
        content: '';
        position: absolute;
        top: 8px;
        right: 8px;
        width: 32px;
        height: 32px;
        border: 3px solid rgba(0,0,0,0.3);
        border-radius: 50%;
        background: rgba(255,255,255,0.9);
        transition: all 0.2s ease;
        z-index: 100;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      }
      .jibbitz-selectable.selected::before {
        content: '✓';
        background: #0077c8;
        border-color: #0077c8;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        box-shadow: 0 2px 12px rgba(0,119,200,0.4);
      }
      .jibbitz-selectable.selected {
        outline: 3px solid #0077c8 !important;
        outline-offset: 2px;
      }
      .jibbitz-selectable:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }

      /* Ensure cards have proper display for positioning */
      product-card.jibbitz-selectable {
        display: block;
      }

      /* Selected items preview */
      .jibbitz-selection-bar__preview {
        display: flex;
        gap: 8px;
        margin-left: 16px;
      }
      .jibbitz-selection-bar__preview-item {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: #f5f5f5;
        overflow: hidden;
      }
      .jibbitz-selection-bar__preview-item img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      @media (max-width: 768px) {
        .jibbitz-selection-bar {
          flex-direction: column;
          gap: 12px;
          padding: 12px 16px;
        }
        .jibbitz-selection-bar__info {
          flex-direction: column;
          gap: 8px;
        }
        .jibbitz-selection-bar__shoe span {
          font-size: 14px;
        }
      }
    `;
    document.head.appendChild(styles);
  }

  bindProductCards() {
    // Find all product cards on the collection page
    // Common Shopify selectors - including custom elements
    const selectors = [
      "product-card", // Custom element in your theme
      ".card--product", // Class on product cards
      ".product-card",
      ".product-item",
      ".grid-product",
      ".collection-product",
      "[data-product-card]",
      ".product-grid-item",
    ];

    let productCards = [];
    for (const selector of selectors) {
      productCards = document.querySelectorAll(selector);
      if (productCards.length > 0) {
        console.log(
          `Found ${productCards.length} product cards with selector: ${selector}`,
        );
        break;
      }
    }

    if (productCards.length === 0) {
      // Fallback: look for links to products in cards
      const links = document.querySelectorAll('a[href*="/products/"]');
      // Get unique parent containers that might be cards
      const cardSet = new Set();
      links.forEach((link) => {
        const card =
          link.closest(".card") ||
          link.closest('[class*="product"]') ||
          link.parentElement;
        if (card) cardSet.add(card);
      });
      productCards = Array.from(cardSet);
      console.log(`Fallback: Found ${productCards.length} product containers`);
    }

    productCards.forEach((card) => {
      card.classList.add("jibbitz-selectable");
      card.addEventListener("click", (e) => this.handleCardClick(e, card));
    });

    console.log(
      `Jibbitz selector initialized with ${productCards.length} products`,
    );

    // Show the bar
    setTimeout(() => {
      this.selectionBar.classList.add("visible");
    }, 300);
  }

  async handleCardClick(e, card) {
    e.preventDefault();
    e.stopPropagation();

    // Get product data
    const productLink = card.querySelector('a[href*="/products/"]') || card;
    const href = productLink.href || productLink.getAttribute("href");

    if (!href) return;

    // Extract product handle from URL
    const match = href.match(/\/products\/([^/?#]+)/);
    if (!match) return;

    const handle = match[1];

    // Check if already selected
    if (this.selectedJibbitz.has(handle)) {
      this.selectedJibbitz.delete(handle);
      card.classList.remove("selected");
    } else {
      // Fetch product data
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          const variant = product.variants[0];

          this.selectedJibbitz.set(handle, {
            productId: product.id,
            variantId: variant.id,
            title: product.title,
            price: variant.price,
            image: product.images[0] || product.featured_image,
            handle: handle,
          });

          card.classList.add("selected");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    }

    this.updateSelectionBar();
  }

  updateSelectionBar() {
    const count = this.selectedJibbitz.size;
    this.selectionBar.querySelector(".count").textContent = count;

    const continueBtn = this.selectionBar.querySelector(
      ".jibbitz-selection-bar__continue",
    );
    continueBtn.disabled = count === 0;

    // Update preview
    let preview = this.selectionBar.querySelector(
      ".jibbitz-selection-bar__preview",
    );
    if (!preview) {
      preview = document.createElement("div");
      preview.className = "jibbitz-selection-bar__preview";
      this.selectionBar
        .querySelector(".jibbitz-selection-bar__info")
        .appendChild(preview);
    }

    preview.innerHTML =
      Array.from(this.selectedJibbitz.values())
        .slice(0, 5)
        .map(
          (j) =>
            `<div class="jibbitz-selection-bar__preview-item"><img src="${j.image}" alt="${j.title}"></div>`,
        )
        .join("") +
      (count > 5
        ? `<div class="jibbitz-selection-bar__preview-item" style="display:flex;align-items:center;justify-content:center;font-weight:bold;">+${count - 5}</div>`
        : "");
  }

  clearSelection() {
    this.selectedJibbitz.clear();
    document
      .querySelectorAll(".product-card.selected, .jibbitz-selectable.selected")
      .forEach((card) => card.classList.remove("selected"));
    this.updateSelectionBar();
  }

  continueToPersonalization() {
    // Save selected Jibbitz to session storage
    const jibbitzArray = Array.from(this.selectedJibbitz.values());
    sessionStorage.setItem(
      STORAGE_KEYS.SELECTED_JIBBITZ,
      JSON.stringify(jibbitzArray),
    );

    // Navigate to personalization page
    // If no shoe selected, they'll need to select one on the personalization page
    if (this.shoe) {
      window.location.href = "/pages/jibbitz-personalization";
    } else {
      // Redirect to a collection to select a shoe first
      alert(
        "Please select a Crocs shoe first by clicking 'Customize with Jibbitz' on a product page.",
      );
    }
  }
}

customElements.define("jibbitz-collection-selector", JibbitzCollectionSelector);

/**
 * Jibbitz Personalization Component
 * The main personalization interface
 * NOW WITH FREE PLACEMENT - drop Jibbitz anywhere on the shoe!
 */
class JibbitzPersonalization extends HTMLElement {
  constructor() {
    super();

    this.currentStep = 1;
    this.selectedShoe = null;
    this.placedJibbitz = new Map(); // uniqueId -> {jibbitzData, x, y}
    this.jibbitzProducts = [];
    this.placementCounter = 0; // For generating unique IDs
    this.maxPlacements = 26; // Maximum Jibbitz like real Crocs holes
  }

  connectedCallback() {
    this.init();
  }

  async init() {
    // Load shoe and Jibbitz from session storage FIRST
    this.loadFromStorage();

    // If missing data show an error UI — no redirects to avoid reload loops
    if (!this.selectedShoe || !this.jibbitzProducts.length) {
      this.innerHTML = `
        <div style="text-align:center;padding:80px 20px;max-width:500px;margin:0 auto;">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="64" height="64" style="color:#ccc;margin-bottom:20px;display:block;margin-left:auto;margin-right:auto;">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h2 style="font-size:22px;margin-bottom:12px;">Nothing to Personalize</h2>
          <p style="color:#666;margin-bottom:24px;">Please start from a product page — select a size, then click "Customize with Jibbitz™".</p>
          <a href="/collections/all" style="display:inline-block;padding:14px 30px;background:#0077c8;color:#fff;text-decoration:none;border-radius:30px;font-weight:600;">Browse Crocs</a>
        </div>
      `;
      return;
    }

    // We have both shoe and Jibbitz - render personalization
    this.bindElements();
    this.bindEvents();
    this.renderJibbitz();
    this.goToStep(1);
    this.renderShoeCanvas();
  }

  loadFromStorage() {
    // Load selected shoe
    const savedShoe = sessionStorage.getItem(STORAGE_KEYS.SELECTED_SHOE);
    if (savedShoe) {
      this.selectedShoe = JSON.parse(savedShoe);
      console.log("Loaded shoe from storage:", this.selectedShoe);
    }

    // Load selected Jibbitz
    const savedJibbitz = sessionStorage.getItem(STORAGE_KEYS.SELECTED_JIBBITZ);
    if (savedJibbitz) {
      this.jibbitzProducts = JSON.parse(savedJibbitz);
      console.log("Loaded Jibbitz from storage:", this.jibbitzProducts);
    }
  }

  bindElements() {
    this.progressSteps = this.querySelectorAll(".jp-progress__step");
    this.stepSections = this.querySelectorAll(".jp-step");
    this.jibbitzGrid = this.querySelector(".jp-jibbitz-grid");
    this.shoeCanvas = this.querySelector(".jp-shoe-canvas__wrapper");
    this.shoeImage = this.querySelector(".jp-shoe-canvas__image");
    this.reviewSection = this.querySelector(".jp-review__content");
    this.navBtnBack = this.querySelector(".jp-nav-btn--back");
    this.navBtnNext = this.querySelector(".jp-nav-btn--next");
    this.clearBtn = this.querySelector(".jp-action-btn--danger");
    this.navProgressDots = this.querySelectorAll(".jp-nav-progress__dot");

    // Pagination elements
    this.paginationPrev = this.querySelector(".jp-pagination-btn--prev");
    this.paginationNext = this.querySelector(".jp-pagination-btn--next");
    this.paginationCurrent = this.querySelector(".jp-pagination-current");
    this.paginationTotal = this.querySelector(".jp-pagination-total");

    // Initialize pagination
    this.currentJibbitzPage = 1;
    this.jibbitzPerPage = 3;
  }

  bindEvents() {
    // Navigation
    this.navBtnBack?.addEventListener("click", () => this.prevStep());
    this.navBtnNext?.addEventListener("click", () => this.handleNextStep());
    this.clearBtn?.addEventListener("click", () => this.clearAllJibbitz());

    // Pagination
    this.paginationPrev?.addEventListener("click", () =>
      this.goToPrevJibbitzPage(),
    );
    this.paginationNext?.addEventListener("click", () =>
      this.goToNextJibbitzPage(),
    );

    // Prevent default drag behavior on images
    this.addEventListener("dragstart", (e) => {
      if (e.target.classList.contains("jp-jibbitz-item")) {
        this.handleDragStart(e);
      }
    });

    this.addEventListener("dragend", (e) => {
      if (e.target.classList.contains("jp-jibbitz-item")) {
        this.handleDragEnd(e);
      }
    });
  }

  renderJibbitz() {
    if (!this.jibbitzGrid || !this.jibbitzProducts.length) return;

    this.jibbitzGrid.innerHTML = this.jibbitzProducts
      .map((product, index) => {
        const price = this.formatMoney(product.price);

        return `
        <div class="jp-jibbitz-item" 
             draggable="true" 
             data-product-id="${product.productId}"
             data-variant-id="${product.variantId}"
             data-title="${product.title}"
             data-price="${product.price}"
             data-image="${product.image || ""}"
             data-index="${index}">
          <img class="jp-jibbitz-item__image" 
               src="${product.image || ""}" 
               alt="${product.title}"
               draggable="false">
          <div class="jp-jibbitz-item__price">${price}</div>
        </div>
      `;
      })
      .join("");

    // Bind drag events to new elements
    this.jibbitzGrid.querySelectorAll(".jp-jibbitz-item").forEach((item) => {
      item.addEventListener("dragstart", (e) => this.handleDragStart(e));
      item.addEventListener("dragend", (e) => this.handleDragEnd(e));
    });

    // Initialize pagination
    this.updateJibbitzPagination();
  }

  updateJibbitzPagination() {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const items = this.jibbitzGrid.querySelectorAll(".jp-jibbitz-item");
    const totalPages = Math.ceil(items.length / this.jibbitzPerPage);

    // Update pagination info
    if (this.paginationCurrent)
      this.paginationCurrent.textContent = this.currentJibbitzPage;
    if (this.paginationTotal) this.paginationTotal.textContent = totalPages;

    // Update button states
    if (this.paginationPrev) {
      this.paginationPrev.disabled = this.currentJibbitzPage === 1;
    }
    if (this.paginationNext) {
      this.paginationNext.disabled = this.currentJibbitzPage >= totalPages;
    }

    // Show/hide items based on current page
    items.forEach((item, index) => {
      const startIndex = (this.currentJibbitzPage - 1) * this.jibbitzPerPage;
      const endIndex = startIndex + this.jibbitzPerPage;

      if (index >= startIndex && index < endIndex) {
        item.classList.add("visible");
      } else {
        item.classList.remove("visible");
      }
    });
  }

  goToPrevJibbitzPage() {
    if (this.currentJibbitzPage > 1) {
      this.currentJibbitzPage--;
      this.updateJibbitzPagination();
    }
  }

  goToNextJibbitzPage() {
    const items = this.jibbitzGrid.querySelectorAll(".jp-jibbitz-item");
    const totalPages = Math.ceil(items.length / this.jibbitzPerPage);

    if (this.currentJibbitzPage < totalPages) {
      this.currentJibbitzPage++;
      this.updateJibbitzPagination();
    }
  }

  renderShoeCanvas() {
    console.log("Rendering shoe canvas...", this.shoeCanvas, this.selectedShoe);

    if (!this.shoeCanvas || !this.selectedShoe) {
      console.error("Missing canvas or shoe:", {
        canvas: this.shoeCanvas,
        shoe: this.selectedShoe,
      });
      return;
    }

    // Set shoe image
    if (this.shoeImage) {
      this.shoeImage.src = this.selectedShoe.image;
      this.shoeImage.alt = this.selectedShoe.title;
      console.log("Shoe image set:", this.selectedShoe.image);
    } else {
      console.error("Shoe image element not found");
    }

    // Remove existing placed Jibbitz
    this.shoeCanvas
      .querySelectorAll(".jp-placed-jibbitz")
      .forEach((el) => el.remove());

    // Setup canvas as drop zone for FREE PLACEMENT
    this.shoeCanvas.addEventListener("dragover", (e) =>
      this.handleCanvasDragOver(e),
    );
    this.shoeCanvas.addEventListener("dragleave", (e) =>
      this.handleCanvasDragLeave(e),
    );
    this.shoeCanvas.addEventListener("drop", (e) => this.handleCanvasDrop(e));

    // Add drop hint overlay
    if (!this.shoeCanvas.querySelector(".jp-drop-hint")) {
      const dropHint = document.createElement("div");
      dropHint.className = "jp-drop-hint";
      dropHint.innerHTML = "<span>Drop Jibbitz here!</span>";
      this.shoeCanvas.appendChild(dropHint);
    }

    // Create trash zone for deleting jibbitz
    this.createTrashZone();

    // Setup touch drag for mobile
    this.setupTouchDrag();

    // Setup mobile navigation auto-hide
    this.setupMobileNavigation();

    console.log("Shoe canvas rendered with FREE PLACEMENT mode");
  }

  addPlacementZoneOverlay() {
    // Remove existing overlay
    this.shoeCanvas?.querySelector(".jp-placement-zone-overlay")?.remove();

    const orientation = this.selectedShoe?.orientation || "up";

    // Create overlay to show restricted zone
    const overlay = document.createElement("div");
    overlay.className = "jp-placement-zone-overlay";

    if (orientation === "up") {
      // Dim the bottom 60%
      overlay.style.cssText = `
        position: absolute;
        left: 0;
        top: 40%;
        width: 100%;
        height: 60%;
        background: rgba(0, 0, 0, 0.5);
        pointer-events: none;
        z-index: 1;
        transition: opacity 0.3s ease;
      `;
    } else if (orientation === "down") {
      // Dim the top 60%
      overlay.style.cssText = `
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 60%;
        background: rgba(0, 0, 0, 0.5);
        pointer-events: none;
        z-index: 1;
        transition: opacity 0.3s ease;
      `;
    }

    // Add a label
    const label = document.createElement("div");
    label.className = "jp-zone-label";
    label.textContent =
      orientation === "up"
        ? "✓ Place Jibbitz here (holes are on top)"
        : "✓ Place Jibbitz here (holes are on bottom)";
    label.style.cssText = `
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      ${orientation === "up" ? "top: 20%;" : "bottom: 20%;"}
      background: rgba(0, 119, 200, 0.9);
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      pointer-events: none;
      z-index: 2;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    `;

    this.shoeCanvas.appendChild(overlay);
    this.shoeCanvas.appendChild(label);

    // Hide overlay after a few seconds
    setTimeout(() => {
      overlay.style.opacity = "0";
      label.style.opacity = "0";
      setTimeout(() => {
        overlay.remove();
        label.remove();
      }, 300);
    }, 3000);
  }

  createTrashZone() {
    // Remove existing trash zone
    document.querySelector(".jp-trash-zone")?.remove();

    // Create trash zone element
    this.trashZone = document.createElement("div");
    this.trashZone.className = "jp-trash-zone";
    this.trashZone.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        <line x1="10" y1="11" x2="10" y2="17"></line>
        <line x1="14" y1="11" x2="14" y2="17"></line>
      </svg>
      <span>Delete</span>
    `;
    document.body.appendChild(this.trashZone);

    // Trash zone drop events
    this.trashZone.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      this.trashZone.classList.add("drag-over");
    });

    this.trashZone.addEventListener("dragleave", () => {
      this.trashZone.classList.remove("drag-over");
    });

    this.trashZone.addEventListener("drop", (e) => {
      e.preventDefault();
      this.trashZone.classList.remove("drag-over");

      const placementId = e.dataTransfer.getData("text/plain");
      if (placementId && this.placedJibbitz.has(placementId)) {
        this.removePlacedJibbitz(placementId);
      }

      this.hideTrashZone();
    });
  }

  showTrashZone() {
    this.trashZone?.classList.add("visible");
  }

  hideTrashZone() {
    this.trashZone?.classList.remove("visible", "drag-over");
  }

  setupTouchDrag() {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    // State for touch drag
    this.touchDragState = {
      active: false,
      jibbitzData: null,
      placementId: null,
      ghost: null,
      startX: 0,
      startY: 0,
    };

    // Setup tap-to-select for jibbitz items
    this.jibbitzGrid?.querySelectorAll(".jp-jibbitz-item").forEach((item) => {
      item.addEventListener(
        "touchstart",
        (e) => this.handleJibbitzTouchStart(e, item),
        { passive: true },
      );
      item.addEventListener(
        "touchmove",
        (e) => this.handleJibbitzTouchMove(e, item),
        { passive: false },
      );
      item.addEventListener("touchend", (e) =>
        this.handleJibbitzTouchEnd(e, item),
      );
    });

    // Setup touch drag for placed jibbitz
    this.shoeCanvas?.addEventListener(
      "touchstart",
      (e) => this.handleCanvasTouchStart(e),
      { passive: true },
    );
    this.shoeCanvas?.addEventListener(
      "touchmove",
      (e) => this.handleCanvasTouchMove(e),
      { passive: false },
    );
    this.shoeCanvas?.addEventListener("touchend", (e) =>
      this.handleCanvasTouchEnd(e),
    );

    // Show hint on first visit
    this.showMobileHint();
  }

  showMobileHint() {
    const hasSeenHint = sessionStorage.getItem("jibbitz_mobile_hint_seen");
    if (hasSeenHint) return;

    setTimeout(() => {
      this.shoeCanvas?.classList.add("show-hint");
      sessionStorage.setItem("jibbitz_mobile_hint_seen", "true");
      setTimeout(() => this.shoeCanvas?.classList.remove("show-hint"), 4000);
    }, 1000);
  }

  setupMobileNavigation() {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    // Find navigation container
    const navContainer = this.querySelector(".jp-navigation");
    if (!navContainer) return;

    let lastScrollTop = 0;
    let scrollTimer = null;

    // Initially hide the navigation on mobile
    navContainer.style.transform = "translateY(100%)";
    navContainer.style.transition = "transform 0.3s ease";

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      // Check if scrolling down
      if (scrollTop > lastScrollTop && scrollTop > 50) {
        // Scrolling down - show navigation
        navContainer.style.transform = "translateY(0)";

        // Clear existing timer
        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }

        // Set timer to hide after 1.5 seconds of no scrolling
        scrollTimer = setTimeout(() => {
          navContainer.style.transform = "translateY(100%)";
        }, 1500);
      }

      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup on component disconnect (if needed)
    this.cleanupMobileNav = () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }

  handleJibbitzTouchStart(e, item) {
    this.touchDragState.startX = e.touches[0].clientX;
    this.touchDragState.startY = e.touches[0].clientY;
    this.touchDragState.jibbitzData = {
      productId: item.dataset.productId,
      variantId: item.dataset.variantId,
      title: item.dataset.title,
      price: item.dataset.price,
      image: item.dataset.image,
    };
  }

  handleJibbitzTouchMove(e, item) {
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchDragState.startX);
    const deltaY = Math.abs(touch.clientY - this.touchDragState.startY);

    // Start drag if moved more than 10px
    if ((deltaX > 10 || deltaY > 10) && !this.touchDragState.active) {
      e.preventDefault();
      this.touchDragState.active = true;
      item.classList.add("touch-dragging");
      this.createTouchGhost(
        this.touchDragState.jibbitzData.image,
        touch.clientX,
        touch.clientY,
      );
    }

    if (this.touchDragState.active) {
      e.preventDefault();
      this.moveTouchGhost(touch.clientX, touch.clientY);
    }
  }

  handleJibbitzTouchEnd(e, item) {
    item.classList.remove("touch-dragging");

    if (this.touchDragState.active) {
      const touch = e.changedTouches[0];

      // Check if dropped on canvas
      const canvasRect = this.shoeCanvas.getBoundingClientRect();
      if (
        touch.clientX >= canvasRect.left &&
        touch.clientX <= canvasRect.right &&
        touch.clientY >= canvasRect.top &&
        touch.clientY <= canvasRect.bottom
      ) {
        const x = ((touch.clientX - canvasRect.left) / canvasRect.width) * 100;
        const y = ((touch.clientY - canvasRect.top) / canvasRect.height) * 100;

        if (!this.checkCollision(x, y)) {
          this.placeJibbitzFree(this.touchDragState.jibbitzData, x, y);
        } else {
          this.showToast("Too close to another Jibbitz!", "error");
        }
      }

      this.removeTouchGhost();
      this.touchDragState.active = false;
    }
  }

  handleCanvasTouchStart(e) {
    const target = e.target.closest(".jp-placed-jibbitz");
    if (target) {
      this.touchDragState.placementId = target.dataset.placementId;
      this.touchDragState.startX = e.touches[0].clientX;
      this.touchDragState.startY = e.touches[0].clientY;
    }
  }

  handleCanvasTouchMove(e) {
    if (!this.touchDragState.placementId) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchDragState.startX);
    const deltaY = Math.abs(touch.clientY - this.touchDragState.startY);

    if ((deltaX > 5 || deltaY > 5) && !this.touchDragState.active) {
      e.preventDefault();
      this.touchDragState.active = true;
      const data = this.placedJibbitz.get(this.touchDragState.placementId);
      if (data) {
        this.createTouchGhost(data.image, touch.clientX, touch.clientY);
        this.showTrashZone();
      }
    }

    if (this.touchDragState.active) {
      e.preventDefault();
      this.moveTouchGhost(touch.clientX, touch.clientY);

      // Check if over trash zone
      const trashRect = this.trashZone?.getBoundingClientRect();
      if (
        trashRect &&
        touch.clientX >= trashRect.left &&
        touch.clientX <= trashRect.right &&
        touch.clientY >= trashRect.top &&
        touch.clientY <= trashRect.bottom
      ) {
        this.trashZone.classList.add("drag-over");
      } else {
        this.trashZone?.classList.remove("drag-over");
      }
    }
  }

  handleCanvasTouchEnd(e) {
    if (this.touchDragState.active && this.touchDragState.placementId) {
      const touch = e.changedTouches[0];

      // Check if dropped on trash zone
      const trashRect = this.trashZone?.getBoundingClientRect();
      if (
        trashRect &&
        touch.clientX >= trashRect.left &&
        touch.clientX <= trashRect.right &&
        touch.clientY >= trashRect.top &&
        touch.clientY <= trashRect.bottom
      ) {
        this.removePlacedJibbitz(this.touchDragState.placementId);
      } else {
        // Reposition on canvas
        const canvasRect = this.shoeCanvas.getBoundingClientRect();
        if (
          touch.clientX >= canvasRect.left &&
          touch.clientX <= canvasRect.right &&
          touch.clientY >= canvasRect.top &&
          touch.clientY <= canvasRect.bottom
        ) {
          const x =
            ((touch.clientX - canvasRect.left) / canvasRect.width) * 100;
          const y =
            ((touch.clientY - canvasRect.top) / canvasRect.height) * 100;

          if (!this.checkCollision(x, y, this.touchDragState.placementId)) {
            this.repositionJibbitz(this.touchDragState.placementId, x, y);
          }
        }
      }

      this.removeTouchGhost();
      this.hideTrashZone();
    }

    this.touchDragState.active = false;
    this.touchDragState.placementId = null;
  }

  createTouchGhost(imageSrc, x, y) {
    this.removeTouchGhost();

    const ghost = document.createElement("div");
    ghost.className = "jp-touch-ghost";
    ghost.innerHTML = `<img src="${imageSrc}" alt="Dragging">`;
    ghost.style.left = x + "px";
    ghost.style.top = y + "px";
    document.body.appendChild(ghost);
    this.touchDragState.ghost = ghost;
  }

  moveTouchGhost(x, y) {
    if (this.touchDragState.ghost) {
      this.touchDragState.ghost.style.left = x + "px";
      this.touchDragState.ghost.style.top = y + "px";
    }
  }

  removeTouchGhost() {
    this.touchDragState.ghost?.remove();
    this.touchDragState.ghost = null;
  }

  handleCanvasDragOver(e) {
    e.preventDefault();

    // Check if we're repositioning (move) or adding new (copy)
    if (e.dataTransfer.types.includes("text/plain")) {
      // Repositioning existing Jibbitz
      e.dataTransfer.dropEffect = "move";
    } else {
      // Adding new Jibbitz
      e.dataTransfer.dropEffect = "copy";
    }

    this.shoeCanvas.classList.add("drag-over");
  }

  handleCanvasDragLeave(e) {
    // Only remove class if leaving the canvas entirely
    if (!this.shoeCanvas.contains(e.relatedTarget)) {
      this.shoeCanvas.classList.remove("drag-over");
    }
  }

  handleCanvasDrop(e) {
    e.preventDefault();
    this.shoeCanvas.classList.remove("drag-over");

    // Calculate drop position as percentage
    const rect = this.shoeCanvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    // Check if this is repositioning an existing placement
    const placementId = e.dataTransfer.getData("text/plain");
    if (placementId && this.placedJibbitz.has(placementId)) {
      // Repositioning existing Jibbitz
      this.repositionJibbitz(placementId, x, y);
      return;
    }

    // Check max placements for new Jibbitz
    if (this.placedJibbitz.size >= this.maxPlacements) {
      this.showToast(`Maximum ${this.maxPlacements} Jibbitz allowed!`, "error");
      return;
    }

    try {
      const jibbitzData = JSON.parse(
        e.dataTransfer.getData("application/json"),
      );

      // Check for collision before placing
      if (this.checkCollision(x, y)) {
        this.showToast(
          "Too close to another Jibbitz! Try a different spot.",
          "error",
        );
        return;
      }

      this.placeJibbitzFree(jibbitzData, x, y);
    } catch (error) {
      console.error("Error placing Jibbitz:", error);
    }
  }

  repositionJibbitz(placementId, x, y) {
    const data = this.placedJibbitz.get(placementId);
    if (!data) return;

    // Check if placement is in allowed zone based on orientation
    if (!this.isPlacementInAllowedZone(y)) {
      const orientation = this.selectedShoe?.orientation || "up";
      const zoneText = orientation === "up" ? "upper area" : "lower area";
      this.showToast(
        `Jibbitz can only be placed on the ${zoneText} where the holes are!`,
        "error",
      );
      return;
    }

    // Check for collision before repositioning (exclude self)
    if (this.checkCollision(x, y, placementId)) {
      this.showToast(
        "Too close to another Jibbitz! Try a different spot.",
        "error",
      );
      return;
    }

    // Update stored position
    data.x = x;
    data.y = y;

    // Update visual element
    const el = this.shoeCanvas.querySelector(
      `[data-placement-id="${placementId}"]`,
    );
    if (el) {
      el.style.left = `${x}%`;
      el.style.top = `${y}%`;
    }
  }

  handleMobilePlacement(detail) {
    const { jibbitzData, x, y } = detail;

    // Check max placements
    if (this.placedJibbitz.size >= this.maxPlacements) {
      this.showToast(`Maximum ${this.maxPlacements} Jibbitz allowed!`, "error");
      return;
    }

    // Check for collision
    if (this.checkCollision(x, y)) {
      this.showToast(
        "Too close to another Jibbitz! Try a different spot.",
        "error",
      );
      return;
    }

    this.placeJibbitzFree(jibbitzData, x, y);
  }

  placeJibbitzFree(jibbitzData, x, y) {
    // Check if placement is in allowed zone based on orientation
    if (!this.isPlacementInAllowedZone(y)) {
      const orientation = this.selectedShoe?.orientation || "up";
      const zoneText = orientation === "up" ? "upper area" : "lower area";
      this.showToast(
        `Jibbitz can only be placed on the ${zoneText} where the holes are!`,
        "error",
      );
      return;
    }

    // Generate unique placement ID
    const placementId = `p${++this.placementCounter}-${Date.now()}`;

    // Store placement data
    this.placedJibbitz.set(placementId, {
      ...jibbitzData,
      x: x,
      y: y,
    });

    // Create visual element
    const jibbitzEl = document.createElement("div");
    jibbitzEl.className = "jp-placed-jibbitz";
    jibbitzEl.dataset.placementId = placementId;
    jibbitzEl.style.left = `${x}%`;
    jibbitzEl.style.top = `${y}%`;
    jibbitzEl.draggable = true;

    jibbitzEl.innerHTML = `
      <img src="${jibbitzData.image}" alt="${jibbitzData.title}" draggable="false">
      <button class="jp-placed-jibbitz__remove" type="button">&times;</button>
    `;

    // Bind events for repositioning and removal
    jibbitzEl
      .querySelector(".jp-placed-jibbitz__remove")
      .addEventListener("click", (e) => {
        e.stopPropagation();
        this.removePlacedJibbitz(placementId);
      });

    // Enable repositioning via drag
    jibbitzEl.addEventListener("dragstart", (e) =>
      this.handlePlacedDragStart(e, placementId),
    );
    jibbitzEl.addEventListener("dragend", (e) => this.handlePlacedDragEnd(e));

    this.shoeCanvas.appendChild(jibbitzEl);
    this.showToast(`${jibbitzData.title} added!`);
    this.updatePlacementCount();
  }

  handlePlacedDragStart(e, placementId) {
    e.target.classList.add("dragging");

    // Show trash zone when dragging placed jibbitz
    this.showTrashZone();

    // Create transparent drag image
    const img = e.target.querySelector("img");
    if (img) {
      const dragImg = img.cloneNode(true);
      dragImg.style.cssText = `
        width: 80px !important;
        height: 80px !important;
        background: transparent !important;
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
        display: block !important;
        border-radius: 0 !important;
        position: fixed !important;
        top: -9999px !important;
        left: -9999px !important;
      `;
      document.body.appendChild(dragImg);
      e.dataTransfer.setDragImage(dragImg, 40, 40);
      setTimeout(() => dragImg.remove(), 0);
    }

    e.dataTransfer.setData("text/plain", placementId);
    e.dataTransfer.effectAllowed = "move";
  }

  handlePlacedDragEnd(e) {
    e.target.classList.remove("dragging");
    this.hideTrashZone();
  }

  removePlacedJibbitz(placementId) {
    const data = this.placedJibbitz.get(placementId);
    this.placedJibbitz.delete(placementId);

    const el = this.shoeCanvas.querySelector(
      `[data-placement-id="${placementId}"]`,
    );
    if (el) {
      el.remove();
    }

    if (data) {
      this.showToast(`${data.title} removed`);
    }
    this.updatePlacementCount();
  }

  updatePlacementCount() {
    const countEl = this.querySelector(".jp-placement-count");
    if (countEl) {
      countEl.textContent = `${this.placedJibbitz.size} / ${this.maxPlacements}`;
    }
  }

  isPlacementInAllowedZone(y) {
    // Check if placement is allowed based on shoe orientation
    // y is percentage from top (0-100)
    const orientation = this.selectedShoe?.orientation || "up";

    if (orientation === "up") {
      // Holes at top - only allow placement in upper 40% (0-40%)
      return y <= 40;
    } else if (orientation === "down") {
      // Holes at bottom - only allow placement in lower 40% (60-100%)
      return y >= 60;
    }

    // Default: allow anywhere
    return true;
  }

  checkCollision(x, y, excludeId = null) {
    // Check if new position would collide with existing Jibbitz
    // Using 12% as the collision radius (80px Jibbitz on typical 600px canvas)
    const collisionRadius = 12;

    for (const [id, jibbitz] of this.placedJibbitz.entries()) {
      if (id === excludeId) continue; // Skip self when repositioning

      const dx = Math.abs(jibbitz.x - x);
      const dy = Math.abs(jibbitz.y - y);
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < collisionRadius) {
        return true; // Collision detected
      }
    }
    return false; // No collision
  }

  handleDragStart(e) {
    const item = e.target.closest(".jp-jibbitz-item");
    if (!item) return;

    item.classList.add("dragging");

    // Create transparent drag image to avoid white background
    const dragImg = item.querySelector("img").cloneNode(true);
    dragImg.style.cssText = `
      width: 80px !important;
      height: 80px !important;
      background: transparent !important;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
      display: block !important;
      border-radius: 0 !important;
      position: fixed !important;
      top: -9999px !important;
      left: -9999px !important;
    `;
    document.body.appendChild(dragImg);
    e.dataTransfer.setDragImage(dragImg, 40, 40);
    setTimeout(() => dragImg.remove(), 0);

    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        productId: item.dataset.productId,
        variantId: item.dataset.variantId,
        title: item.dataset.title,
        price: item.dataset.price,
        image: item.dataset.image,
      }),
    );

    e.dataTransfer.effectAllowed = "copy";
  }

  handleDragEnd(e) {
    const item = e.target.closest(".jp-jibbitz-item");
    if (item) {
      item.classList.remove("dragging");
    }
  }

  clearAllJibbitz() {
    this.placedJibbitz.clear();

    // Remove all placed Jibbitz elements
    this.shoeCanvas?.querySelectorAll(".jp-placed-jibbitz").forEach((el) => {
      el.remove();
    });

    this.updatePlacementCount();
    this.showToast("All Jibbitz cleared");
  }

  goToStep(step) {
    this.currentStep = step;
    this.updateProgress();
    this.updateStepVisibility();
    this.updateNavigation();
  }

  prevStep() {
    if (this.currentStep === 1) {
      // Going back from personalization - go to Jibbitz selection page
      window.location.href = "/pages/jibbitz-selection";
      return;
    }
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }

  handleNextStep() {
    if (this.currentStep === 1) {
      this.goToStep(2);
      this.renderReview();
    } else if (this.currentStep === 2) {
      this.addAllToCart();
    }
  }

  updateProgress() {
    this.progressSteps?.forEach((step, index) => {
      const stepNum = index + 1;
      step.classList.remove("active", "completed");

      if (stepNum < this.currentStep) {
        step.classList.add("completed");
      } else if (stepNum === this.currentStep) {
        step.classList.add("active");
      }
    });

    this.navProgressDots?.forEach((dot, index) => {
      const stepNum = index + 1;
      dot.classList.remove("active", "completed");

      if (stepNum < this.currentStep) {
        dot.classList.add("completed");
      } else if (stepNum === this.currentStep) {
        dot.classList.add("active");
      }
    });
  }

  updateStepVisibility() {
    this.stepSections?.forEach((section, index) => {
      section.classList.toggle("active", index + 1 === this.currentStep);
    });
  }

  updateNavigation() {
    if (this.navBtnBack) {
      this.navBtnBack.style.visibility =
        this.currentStep > 1 ? "visible" : "visible";
    }

    if (this.navBtnNext) {
      if (this.currentStep === 2) {
        this.navBtnNext.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
            <rect x="9" y="3" width="6" height="4" rx="1"/>
            <path d="m9 14 2 2 4-4"/>
          </svg>
          ADD TO CART
        `;
        this.navBtnNext.classList.remove("jp-nav-btn--next");
        this.navBtnNext.classList.add("jp-nav-btn--add-cart");
      } else {
        this.navBtnNext.innerHTML = `
          NEXT
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        `;
        this.navBtnNext.classList.add("jp-nav-btn--next");
        this.navBtnNext.classList.remove("jp-nav-btn--add-cart");
      }
    }
  }

  renderReview() {
    if (!this.reviewSection) return;

    const shoePrice = this.selectedShoe?.price || 0;
    let totalPrice = shoePrice;

    let itemsHtml = "";

    if (this.selectedShoe) {
      itemsHtml += `
        <div class="jp-review__item">
          <img class="jp-review__item-image" src="${this.selectedShoe.image}" alt="${this.selectedShoe.title}">
          <div class="jp-review__item-info">
            <div class="jp-review__item-title">${this.selectedShoe.title}</div>
            <div class="jp-review__item-type">Crocs Shoe</div>
          </div>
          <div class="jp-review__item-price">${this.formatMoney(shoePrice)}</div>
        </div>
      `;
    }

    // Group identical placed Jibbitz
    {
      // Regular Jibbitz flow - group identical placed Jibbitz
      const jibbitzGroups = new Map();
      this.placedJibbitz.forEach((jibbitz) => {
        const key = jibbitz.variantId;
        if (jibbitzGroups.has(key)) {
          jibbitzGroups.get(key).count++;
        } else {
          jibbitzGroups.set(key, { ...jibbitz, count: 1, placed: true });
        }
      });

      // Add unplaced Jibbitz (selected but not on shoe) with count 1
      this.jibbitzProducts.forEach((jibbitz) => {
        const key = jibbitz.variantId.toString();
        if (!jibbitzGroups.has(key) && !jibbitzGroups.has(parseInt(key))) {
          jibbitzGroups.set(key, { ...jibbitz, count: 1, placed: false });
        }
      });

      jibbitzGroups.forEach((jibbitz) => {
        const price = parseInt(jibbitz.price) || 0;
        const itemTotal = price * jibbitz.count;
        totalPrice += itemTotal;

        const typeLabel = jibbitz.placed
          ? `Jibbitz Charm${jibbitz.count > 1 ? ` <span style="font-weight: 700; color: #0077c8;">× ${jibbitz.count}</span>` : ""}`
          : `Jibbitz Charm (not placed)`;

        itemsHtml += `
          <div class="jp-review__item">
            <img class="jp-review__item-image" src="${jibbitz.image}" alt="${jibbitz.title}">
            <div class="jp-review__item-info">
              <div class="jp-review__item-title">${jibbitz.title}</div>
              <div class="jp-review__item-type">${typeLabel}</div>
            </div>
            <div class="jp-review__item-price">${this.formatMoney(itemTotal)}</div>
          </div>
        `;
      });
    }

    // Generate customized shoe preview with placed jibbitz
    let placedJibbitzHtml = "";
    this.placedJibbitz.forEach((jibbitz, placementId) => {
      placedJibbitzHtml += `
        <div class="jp-review-jibbitz" style="position: absolute; left: ${jibbitz.x}%; top: ${jibbitz.y}%; transform: translate(-50%, -50%); width: 50px; height: 50px; z-index: 5;">
          <img src="${jibbitz.image}" alt="${jibbitz.title}" style="width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
        </div>
      `;
    });

    this.reviewSection.innerHTML = `
      <div class="jp-review__preview">
        <div style="position: relative; width: 100%; max-width: 100%;">
          <img class="jp-review__preview-image" src="${this.selectedShoe?.image || ""}" alt="Your customized Crocs" style="width: 100%; display: block;">
          ${placedJibbitzHtml}
        </div>
      </div>
      <div class="jp-review__summary">
        ${itemsHtml}
        <div class="jp-review__total">
          <span class="jp-review__total-label">Total</span>
          <span class="jp-review__total-price">${this.formatMoney(totalPrice)}</span>
        </div>
      </div>
    `;
  }

  async addAllToCart() {
    const items = [];

    if (this.selectedShoe) {
      items.push({
        id: this.selectedShoe.variantId,
        quantity: 1,
        properties: {
          _personalized: "true",
          _jibbitz_count: this.placedJibbitz.size.toString(),
        },
      });
    }

    // Add individual Jibbitz
    const jibbitzCounts = new Map();
    this.placedJibbitz.forEach((jibbitz) => {
      const variantId = jibbitz.variantId;
      if (variantId && !isNaN(parseInt(variantId))) {
        jibbitzCounts.set(variantId, (jibbitzCounts.get(variantId) || 0) + 1);
      }
    });

    // Add unplaced Jibbitz (selected but not placed on shoe) - 1 each
    this.jibbitzProducts.forEach((jibbitz) => {
      const variantId = jibbitz.variantId;
      if (variantId && !isNaN(parseInt(variantId))) {
        if (
          !jibbitzCounts.has(variantId.toString()) &&
          !jibbitzCounts.has(parseInt(variantId))
        ) {
          jibbitzCounts.set(variantId, 1);
        }
      }
    });

    jibbitzCounts.forEach((quantity, variantId) => {
      items.push({
        id: parseInt(variantId),
        quantity: quantity,
      });
    });

    try {
      this.navBtnNext.disabled = true;
      this.navBtnNext.innerHTML = "Adding...";

      console.log("Adding items to cart:", items);

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: items }),
      });

      if (response.ok) {
        // Clear session storage
        sessionStorage.removeItem(STORAGE_KEYS.SELECTED_SHOE);
        sessionStorage.removeItem(STORAGE_KEYS.SELECTED_JIBBITZ);

        this.showToast("Added to cart!", "success");

        setTimeout(() => {
          window.location.href = "/cart";
        }, 1000);
      } else {
        const errorData = await response.json();
        console.error("Cart error response:", errorData);
        throw new Error(errorData.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      console.error("Items that failed:", items);
      this.showToast("Failed to add to cart. Please try again.", "error");
      this.navBtnNext.disabled = false;
      this.updateNavigation();
    }
  }

  formatMoney(cents) {
    const amount = (parseInt(cents) / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || "JOD";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  }

  showToast(message, type = "default") {
    const existingToast = document.querySelector(".jp-toast");
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement("div");
    toast.className = `jp-toast jp-toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("show"), 10);

    setTimeout(() => {
      toast.classList.remove("show");
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }
}

customElements.define("jibbitz-personalization", JibbitzPersonalization);

/**
 * Jibbitz Selection Page Component
 * Handles the dedicated Jibbitz selection page with filtered products
 */
class JibbitzSelectionPage extends HTMLElement {
  constructor() {
    super();
    this.selectedJibbitz = new Map();
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;
    this.hasMoreProducts = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Check if we have a shoe saved
    const savedShoe = sessionStorage.getItem(STORAGE_KEYS.SELECTED_SHOE);
    if (savedShoe) {
      this.shoe = JSON.parse(savedShoe);
      this.showShoePreview();
    } else {
      // Show no shoe warning
      this.showNoShoeWarning();
    }

    // Get pagination data from element attributes
    this.currentPage = parseInt(this.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.dataset.totalPages) || 1;
    this.hasMoreProducts = this.dataset.hasNext === "true";

    this.bindElements();
    this.bindEvents();
    this.setupInfiniteScroll();
  }

  showNoShoeWarning() {
    const warning = document.getElementById("jbs-no-shoe-warning");
    if (warning) {
      warning.style.display = "block";
    }
  }

  showShoePreview() {
    const preview = document.getElementById("jbs-shoe-preview");
    const image = document.getElementById("jbs-shoe-image");
    const title = document.getElementById("jbs-shoe-title");

    if (preview && this.shoe) {
      preview.style.display = "flex";
      if (image) {
        image.src = this.shoe.image;
        image.alt = this.shoe.title;
      }
      if (title) {
        title.textContent = `Customizing: ${this.shoe.title}`;
      }
    }
  }

  bindElements() {
    this.grid = document.getElementById("jbs-grid");

    this.items = this.grid
      ? this.grid.querySelectorAll(".jbs-item")
      : this.querySelectorAll(".jbs-item");

    this.countEl = document.getElementById("jbs-count");
    this.clearBtn = document.getElementById("jbs-clear");
    this.continueBtn = document.getElementById("jbs-continue");
    this.loadingEl = document.getElementById("jbs-loading");
    this.sentinelEl = document.getElementById("jbs-sentinel");
    this.loadMoreBtn = document.getElementById("jbs-load-more-btn");
    this.loadMoreContainer = document.getElementById("jbs-load-more");
  }

  bindEvents() {
    // Item selection
    this.items.forEach((item) => {
      item.addEventListener("click", () => this.toggleItem(item));
    });

    // Clear button
    this.clearBtn?.addEventListener("click", () => this.clearAll());

    // Continue button
    this.continueBtn?.addEventListener("click", () =>
      this.continueToPersonalization(),
    );

    // Load more button
    this.loadMoreBtn?.addEventListener("click", () => this.loadMoreProducts());
  }

  setupInfiniteScroll() {
    // Only setup if there are more pages
    if (!this.hasMoreProducts || !this.sentinelEl) return;

    const options = {
      root: null,
      rootMargin: "800px", // Start loading when 800px away from sentinel
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.hasMoreProducts) {
          this.loadMoreProducts();
        }
      });
    }, options);

    this.observer.observe(this.sentinelEl);
  }

  async loadMoreProducts() {
    if (this.isLoading || !this.hasMoreProducts) return;

    this.isLoading = true;
    this.showLoading(true);

    try {
      const nextPage = this.currentPage + 1;
      // Use Section Rendering API - fetch the AJAX section with next page
      const url = `${window.location.pathname}?section_id=jibbitz-grid-ajax&page=${nextPage}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch products");

      const html = await response.text();

      // Parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Get all product items from the response
      const newItems = doc.querySelectorAll(".jbs-item");

      // Get pagination data from the response
      const paginationScript = doc.querySelector(".jbs-pagination-data");
      let paginationData = null;
      if (paginationScript) {
        try {
          paginationData = JSON.parse(paginationScript.textContent);
        } catch (e) {
          console.error("Error parsing pagination data:", e);
        }
      }

      if (newItems.length === 0) {
        this.hasMoreProducts = false;
        this.hideLoadMore();
      } else {
        // Add new items to the grid
        newItems.forEach((item) => {
          const clonedItem = item.cloneNode(true);

          // Bind click event to new item
          clonedItem.addEventListener("click", () =>
            this.toggleItem(clonedItem),
          );
          this.grid.appendChild(clonedItem);
        });

        this.currentPage = nextPage;

        // Update pagination state
        if (paginationData) {
          this.hasMoreProducts = paginationData.hasNext;
          this.totalPages = paginationData.totalPages;
        } else {
          // Fallback: check if we've reached the last page
          this.hasMoreProducts = nextPage < this.totalPages;
        }

        if (!this.hasMoreProducts) {
          this.hideLoadMore();
        }
      }
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      this.isLoading = false;
      this.showLoading(false);
    }
  }

  showLoading(show) {
    if (this.loadingEl) {
      this.loadingEl.style.display = show ? "flex" : "none";
    }
    if (this.loadMoreBtn) {
      this.loadMoreBtn.disabled = show;
      this.loadMoreBtn.textContent = show ? "Loading..." : "Load More Jibbitz";
    }
  }

  hideLoadMore() {
    if (this.loadMoreContainer) {
      this.loadMoreContainer.style.display = "none";
    }
    if (this.observer && this.sentinelEl) {
      this.observer.unobserve(this.sentinelEl);
    }
  }

  toggleItem(item) {
    const id = item.dataset.productId;
    item.classList.toggle("selected");

    if (item.classList.contains("selected")) {
      this.selectedJibbitz.set(id, {
        productId: id,
        variantId: item.dataset.variantId,
        title: item.dataset.title,
        price: parseInt(item.dataset.price),
        image: item.dataset.image,
        handle: item.dataset.handle,
      });
    } else {
      this.selectedJibbitz.delete(id);
    }

    this.updateCount();
  }

  updateCount() {
    if (this.countEl) {
      this.countEl.textContent = this.selectedJibbitz.size;
    }

    // Enable/disable continue button
    if (this.continueBtn) {
      this.continueBtn.disabled = this.selectedJibbitz.size === 0;
    }
  }

  clearAll() {
    this.items.forEach((item) => item.classList.remove("selected"));
    this.selectedJibbitz.clear();
    this.updateCount();
  }

  continueToPersonalization() {
    if (this.selectedJibbitz.size === 0) return;

    // Check if shoe is saved
    const savedShoe = sessionStorage.getItem(STORAGE_KEYS.SELECTED_SHOE);
    if (!savedShoe) {
      alert(
        "Please select a Crocs shoe first by clicking 'Customize with Jibbitz' on a product page.",
      );
      return;
    }

    // Save selected jibbitz
    sessionStorage.setItem(
      STORAGE_KEYS.SELECTED_JIBBITZ,
      JSON.stringify(Array.from(this.selectedJibbitz.values())),
    );

    // Navigate to personalization page
    window.location.href = "/pages/jibbitz-personalization";
  }
}

if (!customElements.get("jibbitz-selection-page")) {
  customElements.define("jibbitz-selection-page", JibbitzSelectionPage);
}

/* =========================================
   7. Mobile Support
   ========================================= */

/**
 * Mobile Touch Support for Jibbitz Personalization
 * Enhances the experience on touch devices
 */
class JibbitzMobileSupport {
  constructor() {
    this.isMobile = window.innerWidth <= 768;
    this.selectedJibbitz = null;
    this.init();
  }

  init() {
    if (!this.isMobile) return;

    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }

    // Re-check on resize
    window.addEventListener("resize", () => {
      this.isMobile = window.innerWidth <= 768;
    });
  }

  setup() {
    this.setupPanelToggle();
    this.setupTouchHint();
  }

  setupPanelToggle() {
    const panel = document.querySelector(".jp-jibbitz-panel");
    if (!panel) return;

    // Create toggle button if not exists
    let header = panel.querySelector(".jp-jibbitz-panel__header");
    if (!header) {
      const title = panel.querySelector(".jp-jibbitz-panel__title");
      const subtitle = panel.querySelector(".jp-jibbitz-panel__subtitle");

      header = document.createElement("div");
      header.className = "jp-jibbitz-panel__header";
      header.innerHTML = `
        <span class="jp-jibbitz-panel__title">${title?.textContent || "Your Jibbitz"}</span>
        <button class="jp-jibbitz-panel__toggle" type="button" aria-label="Toggle panel">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
        </button>
      `;

      // Remove old title/subtitle
      title?.remove();
      subtitle?.remove();

      // Insert header at beginning
      panel.insertBefore(header, panel.firstChild);
    }

    // Toggle collapse on header click
    header.addEventListener("click", () => {
      panel.classList.toggle("collapsed");
    });
  }

  setupTouchHint() {
    // Add first-time user hint
    const hasSeenHint = sessionStorage.getItem("jibbitz_mobile_hint_seen");
    if (hasSeenHint) return;

    const canvas = document.querySelector(".jp-shoe-canvas__wrapper");
    if (!canvas) return;

    // Show hint after a short delay
    setTimeout(() => {
      canvas.classList.add("show-hint");
      sessionStorage.setItem("jibbitz_mobile_hint_seen", "true");
      setTimeout(() => canvas.classList.remove("show-hint"), 4000);
    }, 1500);
  }
}

// Initialize mobile support
if (typeof window !== "undefined") {
  new JibbitzMobileSupport();
}

// Add CSS for mobile support
const mobileStyles = document.createElement("style");
mobileStyles.textContent = `
  @media (max-width: 768px) {
    .jp-jibbitz-panel__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 8px;
      border-bottom: 1px solid #eee;
      margin-bottom: 10px;
    }
    
    .jp-jibbitz-panel__toggle {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #f0f0f0;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    
    .jp-jibbitz-panel__toggle svg {
      width: 18px;
      height: 18px;
      color: #666;
      transition: transform 0.3s ease;
    }
    
    .jp-jibbitz-panel.collapsed .jp-jibbitz-panel__toggle svg {
      transform: rotate(180deg);
    }
    
    .jp-jibbitz-panel.collapsed .jp-jibbitz-grid {
      display: none;
    }
  }
`;
document.head.appendChild(mobileStyles);
